import { useState } from 'react';
import {
  DEFAULT_UTM_HEADER,
  UTM_COOKIE_NAME,
  UTM_DEFAULT_TTL_DAYS,
} from '@/shared/config';

type UtmSet = {
  campaign: string;
  source?: string;
  medium?: string;
};

const PRM_HOURS_REGEX = /_prm-h(\d+)$/;

const getCookie = (name: string) => {
  const row = document.cookie
    .split('; ')
    .find(item => item.startsWith(`${name}=`));
  return row ? decodeURIComponent(row.slice(name.length + 1)) : null;
};

const setCookie = (name: string, value: string, maxAgeSeconds: number) => {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(
    value
  )}; max-age=${maxAgeSeconds}; path=/; SameSite=Lax${secure}`;
};

const parsePrmHours = (campaign: string): number | null => {
  const match = campaign.match(PRM_HOURS_REGEX);
  if (!match) return null;
  const hours = Number(match[1]);
  return Number.isInteger(hours) && hours > 0 ? hours : null;
};

const getStoredUtm = (): UtmSet | null => {
  const raw = getCookie(UTM_COOKIE_NAME);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof (parsed as UtmSet).campaign === 'string'
    ) {
      return parsed as UtmSet;
    }
    return null;
  } catch {
    return null;
  }
};

const captureUtmFromUrl = (search: string) => {
  const params = new URLSearchParams(search);
  const campaign = params.get('utm_campaign');
  if (!campaign) return;

  const stored = getStoredUtm();
  if (stored && parsePrmHours(stored.campaign) !== null) return;

  const utm: UtmSet = { campaign };
  const source = params.get('utm_source');
  const medium = params.get('utm_medium');
  if (source) utm.source = source;
  if (medium) utm.medium = medium;

  const prmHours = parsePrmHours(campaign);
  const maxAgeSeconds =
    prmHours !== null
      ? prmHours * 60 * 60
      : UTM_DEFAULT_TTL_DAYS * 24 * 60 * 60;

  setCookie(UTM_COOKIE_NAME, JSON.stringify(utm), maxAgeSeconds);
};

const isIsolationActive = (search: string) => {
  const urlCampaign = new URLSearchParams(search).get('utm_campaign');
  if (urlCampaign && parsePrmHours(urlCampaign) !== null) return true;

  const stored = getStoredUtm();
  return stored !== null && parsePrmHours(stored.campaign) !== null;
};

const getUtmHeader = () => {
  const stored = getStoredUtm();
  if (!stored) return DEFAULT_UTM_HEADER;

  const params = new URLSearchParams();
  if (stored.source) params.set('utm_source', stored.source);
  if (stored.medium) params.set('utm_medium', stored.medium);
  params.set('utm_campaign', stored.campaign);
  return params.toString();
};

const useUtmTracking = () => {
  const [isIsolated] = useState(() => {
    captureUtmFromUrl(window.location.search);
    return isIsolationActive(window.location.search);
  });

  return { isIsolated };
};

export {
  parsePrmHours,
  captureUtmFromUrl,
  getStoredUtm,
  isIsolationActive,
  getUtmHeader,
  useUtmTracking,
};
